// ⚠️ MIRROR OF hrms-tasks-app/src/utils/retainer.js. CRA cannot import from
// outside client/src, so this copy exists for the same reason lib/leadDays.js
// and lib/indiaStates.js do — and carries the same risk: the editor would total
// a package one way while the invoice bills it another, with nothing erroring.
// tests/retainer.test.js asserts the two answer identically. Change both.

export const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const PER_YEAR = { monthly: 12, quarterly: 4, half_yearly: 2, yearly: 1 };
export const FREQUENCIES = Object.keys(PER_YEAR);

export const FREQUENCY_LABEL = {
  monthly: 'Every month',
  quarterly: 'Every quarter',
  half_yearly: 'Every six months',
  yearly: 'Every year',
};

// Short form for a table column, where "Every month" is three words too many.
export const FREQUENCY_SHORT = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  half_yearly: 'Half-yearly',
  yearly: 'Yearly',
};

export function listTotal(services = []) {
  return r2((services || []).reduce((s, l) => s + (Number(l.amount) || 0), 0));
}

// See the server file for the full reasoning. The short version: the agreed
// figure is what bills, `null` means "no discount", and `== null` is load
// bearing because an agreed 0 is a real figure.
export function computeTotals({ services = [], agreedAmount = null, fallbackAmount = 0 } = {}) {
  const list = listTotal(services);
  const hasServices = (services || []).length > 0;
  const agreed = agreedAmount == null ? null : r2(agreedAmount);

  const amount = agreed != null ? agreed : (hasServices ? list : r2(fallbackAmount));
  const discount = r2(list - amount);

  return {
    listTotal: list,
    agreedAmount: agreed,
    amount,
    discount: hasServices ? discount : 0,
    discountPct: hasServices && list > 0 ? r2((discount / list) * 100) : 0,
  };
}

export function apportion(services = [], target) {
  const lines = services || [];
  if (!lines.length) return [];
  const list = listTotal(lines);
  const want = r2(target);

  if (list <= 0) {
    const even = r2(want / lines.length);
    return lines.map((l, i) => ({
      ...l,
      billedAmount: i === lines.length - 1 ? r2(want - even * (lines.length - 1)) : even,
    }));
  }

  let running = 0;
  return lines.map((l, i) => {
    const last = i === lines.length - 1;
    const billed = last ? r2(want - running) : r2(((Number(l.amount) || 0) / list) * want);
    running = r2(running + billed);
    return { ...l, billedAmount: billed };
  });
}

export const annualValue = (amount, frequency) => r2((Number(amount) || 0) * (PER_YEAR[frequency] || 1));

// ── Display helpers, browser-only (no server counterpart to drift from) ──────

export function money(n, currency = 'INR') {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency || 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: Number.isInteger(Number(n)) ? 0 : 2,
  }).format(Number(n) || 0);
}

// What a rate card's basis says, so a line quoted "per filing" on a monthly
// retainer reads honestly rather than looking like it was priced wrong.
export const BASIS_LABELS = {
  per_filing: 'per filing',
  per_month: 'per month',
  per_quarter: 'per quarter',
  per_year: 'per year',
  one_time: 'one-time',
  hourly: 'per hour',
};

export const basisLabel = (b) => BASIS_LABELS[b] || b || '';

// 1st / 2nd / 3rd / 4th … 11th–13th are the exceptions a naive version gets
// wrong. Mirrors utils/retainer.js#ordinal.
const ORDINALS = ['th', 'st', 'nd', 'rd'];
export function ordinal(n) {
  const v = Number(n) || 0;
  const rem = v % 100;
  return `${v}${ORDINALS[(rem - 20) % 10] || ORDINALS[rem] || ORDINALS[0]}`;
}

// ⚠️ DISPLAY ONLY — the DATE a retainer next bills is computed on the SERVER
// (`nextBillsOn` on the wire, from utils/retainer.js#nextBillingFor) because it
// is the generator's own decision. Working it out here from `frequency` and
// `billingDay` would be a second answer, free to promise a date the cron does
// not act on. This just puts the setting into a sentence.
export function billingDayLabel(billingDay, frequency = 'monthly') {
  const day = Number(billingDay) || 1;
  const after = frequency === 'monthly' ? 'the month' : 'each period';
  return `Raised on the ${ordinal(day)} of the month after ${after} ends`;
}

// 1..31, for the picker. 29-31 are legal and mean "as late as the month allows"
// — the server clamps them, which is why the labels say so.
export const BILLING_DAYS = Array.from({ length: 31 }, (_, i) => {
  const day = i + 1;
  return {
    value: day,
    label: day > 28 ? `${ordinal(day)} (or the last day)` : ordinal(day),
  };
});
